import { useCallback, useMemo, useState } from 'react'
import Dialog from '../../../Common/Dialog/Dialog'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch'
import type { StoreProductStore } from '../../../UserData/_stores/Store/StoreProductStore'
import { Button, Chip, Divider, Grid, Stack, styled, Typography } from '@mui/material'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import type { StoreVariantStore } from '../../../UserData/_stores/Store/StoreVariantStore'
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import BlockIcon from '@mui/icons-material/Block';
import VariantRow from './VariantRow'
import IgnoreStoreProductDialog from './IgnoreStoreProductDialog'
import UnsyncStoreProductDialog from './UnsyncStoreProductDialog'
import { UnignoreAllStoreVariants } from '../../../UserData/_actions/Store/StoreVariantActions'
import { STORE_ICON_URLS } from '../../StoreIconUrls'
import logoSmallUrl from '@resources/img/logo-small.svg?url'

const StoreIcon = styled('img')({
  width: '24px',
  height: '24px',
  objectFit: 'contain',
})

interface Props {
  storeProduct: StoreProductStore
  opened: boolean
  onClose: () => void
}

export default function EditVariantsDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const storeType = useAppSelector(state => state.get('userData').get('stores').get(props.storeProduct.get('id_store'))?.get('slug') ?? '')
  const storeIconUrl = STORE_ICON_URLS[storeType]
  const storeName = useAppSelector(state => state.get('userData').get('stores').get(props.storeProduct.get('id_store'))?.get('name') ?? '')

  const [unsyncDialogOpened, setUnsyncDialogOpened] = useState(false)
  const [ignoreDialogOpened, setIgnoreDialogOpened] = useState(false)
  const variantCounts = useMemo(() => {
    const variants = props.storeProduct.get('listVariants')

    return {
      synced: variants.filter(variant => variant.get('id_product_design') !== null && !variant.get('ignored')).count(),
      notSynced: variants.filter(variant => variant.get('id_product_design') === null && !variant.get('ignored')).count(),
      ignored: variants.filter(variant => variant.get('ignored')).count(),
    }
  }, [props.storeProduct])

  const renderVariant = useCallback((variant: StoreVariantStore) => {
    return <VariantRow
      key={variant.get('id')}
      storeType={storeType}
      variant={variant}
    />
  }, [storeType])

  const isAllIgnored = useMemo(() => {
    return props.storeProduct.get('listVariants').reduce((reduction, variant) => {
      return variant.get('ignored') && reduction
    }, true)
  }, [props.storeProduct])

  const isAllUnsynced = useMemo(() => {
    return props.storeProduct.get('listVariants').reduce((reduction, variant) => {
      return variant.get('id_product_design') === null && reduction
    }, true)
  }, [props.storeProduct])

  const onUnsyncClose = useCallback(() => {
    setUnsyncDialogOpened(false)
  }, [])

  const onUnsyncOpen = useCallback(() => {
    setUnsyncDialogOpened(true)
  }, [])

  const onIgnoreClose = useCallback(() => {
    setIgnoreDialogOpened(false)
  }, [])

  const onIgnoreOpen = useCallback(() => {
    setIgnoreDialogOpened(true)
  }, [])

  const onUnignoreAll = useCallback(() => {
    dispatch(UnignoreAllStoreVariants(storeType, props.storeProduct.get('id')))
  }, [storeType, props.storeProduct.get('id')])

  return <Dialog
    open={props.opened}
    maxWidth="md"
    fullWidth
    onClose={props.onClose}
    showCloseButton
  >
    <DialogTitle>{t('Sync variants')}</DialogTitle>
    <DialogContent>
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="body1">{t('Choose your Art of Where product and options to be associated with each variant of your {{store}} product.', { store: storeName })}</Typography>

          <Stack direction="row" alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              color="success"
              icon={<SyncIcon fontSize="inherit" />}
              label={t('{{count}} Synced', { count: variantCounts.synced })}
              variant="badge"
            />
            <Chip
              color="gray"
              icon={<SyncDisabledIcon fontSize="inherit" />}
              label={t('{{count}} Not synced', { count: variantCounts.notSynced })}
              variant="badge"
            />
            <Chip
              color="warning"
              icon={<BlockIcon fontSize="inherit" />}
              label={t('{{count}} Ignored', { count: variantCounts.ignored })}
              variant="badge"
            />
          </Stack>
        </Stack>

        <Stack spacing={2} divider={<Divider />}>
          <Grid container sx={{ alignItems: 'center' }}>
            <Grid item md={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <StoreIcon src={storeIconUrl} />
                <Typography variant="h3">{storeName}</Typography>
              </Stack>
            </Grid>
            <Grid item md={8}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <StoreIcon src={logoSmallUrl} />
                  <Typography variant="h3">Art of Where</Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {isAllIgnored
                    ? <Button
                      variant="outlined"
                      color="warning"
                      onClick={onUnignoreAll}
                    >{t('Unignore all')}</Button>
                    : <Button
                      variant="outlined"
                      color="warning"
                      onClick={onIgnoreOpen}
                    >{t('Ignore all')}</Button>}


                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onUnsyncOpen}
                    disabled={isAllUnsynced}
                  >{t('Unsync all')}</Button>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <Stack divider={<Divider />}>
            {props.storeProduct.get('listVariants').valueSeq().map(renderVariant)}
          </Stack>
        </Stack>
      </Stack>
    </DialogContent>

    <IgnoreStoreProductDialog
      storeProduct={props.storeProduct}
      opened={ignoreDialogOpened}
      onClose={onIgnoreClose}
    />

    <UnsyncStoreProductDialog
      storeProduct={props.storeProduct}
      opened={unsyncDialogOpened}
      onClose={onUnsyncClose}
    />
  </Dialog>
}
